<script setup lang="ts">
/**
 * AddApiKeyModal — 1:1 порт docs/mockups/modal-add-apikey.html (inbox-185).
 *
 * Спека §19 «Settings · Ключи API»: «Создать ключ» → modal:add-apikey
 * (name + scopes + expiration) · success → modal показывает full key
 * ONCE + copy CTA. plaintext возвращается только из POST /api/v1/api-keys.
 *
 * 2 шага:
 *  1) form    — name + env (display only) + scopes + expiry + warning
 *  2) reveal  — success icon + plaintext-секрет + summary + curl-snippet
 *
 * Открывается из SettingsApiKeysView: modals.open('add-api-key').
 * Refresh listenable через modals.on('add-api-key', refresh).
 *
 * Env-сегменты (Production/Sandbox) — info-only; backend prefix всегда
 * `sk_…`, dev/prod различия пока не реализованы (это маркетинговая
 * метка из мокапа). Не отправляется на сервер.
 *
 * Auto-rotation toggle — info-only; cron-rotation не реализован,
 * сохранение поля не отправляется. Отрисовано чтобы UI совпадал
 * с мокапом + готов к будущей фиче.
 */

import { computed, ref, watch } from 'vue';
import { ApiKeysApi, ApiError, type ApiKey, type ApiKeyCreateOut } from '@/api/settings';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';

const modals = useModalStore();

type Step = 'form' | 'reveal';
type ExpKey = '30' | '90' | '365' | 'custom' | 'never';
type Env = 'live' | 'test';

interface ScopeDef {
  code: string;
  desc: string;
  tier: 'r' | 'w' | 'a';
  danger?: boolean;
}

const SCOPES: ScopeDef[] = [
  { code: 'links:read', desc: 'Чтение ссылок, статистика кликов и UTM.', tier: 'r' },
  { code: 'links:write', desc: 'Создание, изменение и архивирование ссылок.', tier: 'w' },
  { code: 'campaigns:read', desc: 'Чтение кампаний и их статистики.', tier: 'r' },
  { code: 'campaigns:write', desc: 'Создание и редактирование кампаний.', tier: 'w' },
  { code: 'analytics:read', desc: 'Drill-down метрики: гео, devices, A/B, антифрод.', tier: 'r' },
  { code: 'webhooks:manage', desc: 'Подписки на события и журнал доставок.', tier: 'w' },
  { code: 'admin', desc: 'Полный доступ — биллинг, участники, удаление организации.', tier: 'a', danger: true },
];

const PRESETS: { label: string; codes: string[] | 'all-but-admin' }[] = [
  { label: 'Только чтение', codes: ['links:read', 'campaigns:read', 'analytics:read'] },
  { label: 'Read + Write', codes: ['links:read', 'links:write'] },
  { label: 'Все, кроме admin', codes: 'all-but-admin' },
];

const step = ref<Step>('form');
const name = ref('');
const env = ref<Env>('live');
const scopesChecked = ref<Record<string, boolean>>({
  'links:read': true,
  'links:write': true,
  'campaigns:read': false,
  'campaigns:write': false,
  'analytics:read': false,
  'webhooks:manage': false,
  'admin': false,
});
const expKey = ref<ExpKey>('90');
const customDate = ref('');
const autoRotate = ref(true);
const submitting = ref(false);
const error = ref<string | null>(null);
const created = ref<ApiKeyCreateOut | null>(null);
const copied = ref(false);
const tab = ref<'curl' | 'python' | 'node'>('curl');

watch(
  () => modals.activeKey,
  (k) => {
    if (k !== 'add-api-key') return;
    step.value = 'form';
    name.value = '';
    env.value = 'live';
    scopesChecked.value = {
      'links:read': true,
      'links:write': true,
      'campaigns:read': false,
      'campaigns:write': false,
      'analytics:read': false,
      'webhooks:manage': false,
      'admin': false,
    };
    expKey.value = '90';
    customDate.value = '';
    autoRotate.value = true;
    submitting.value = false;
    error.value = null;
    created.value = null;
    copied.value = false;
    tab.value = 'curl';
  },
);

const selectedScopes = computed<string[]>(() =>
  SCOPES.filter((s) => scopesChecked.value[s.code]).map((s) => s.code),
);

const canSubmit = computed(
  () =>
    name.value.trim().length > 0 &&
    name.value.trim().length <= 255 &&
    selectedScopes.value.length > 0 &&
    !submitting.value &&
    (expKey.value !== 'custom' || customDate.value.length > 0),
);

/** Преобразует exp в expires_in_days для backend payload. */
function expiresInDays(): number | null {
  if (expKey.value === 'never') return null;
  if (expKey.value === 'custom') {
    if (!customDate.value) return 90;
    const target = new Date(customDate.value + 'T23:59:59');
    const now = new Date();
    const days = Math.max(1, Math.round((target.getTime() - now.getTime()) / 86400000));
    return Math.min(days, 730); // cap 2 года
  }
  return Number(expKey.value);
}

/** Текст hint'а под expiry pills. */
const expHint = computed<string>(() => {
  if (expKey.value === 'never')
    return 'Без срока — не рекомендуется. Создавать ключи без срока можно только администратору.';
  if (expKey.value === 'custom') return 'Откроется выбор даты — максимум 2 года вперёд.';
  const days = Number(expKey.value);
  const target = new Date(Date.now() + days * 86400000);
  const formatted = target.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const remind = days >= 365 ? 'За 30 дней' : 'За 7 дней';
  return `Ключ перестанет работать ${formatted}. ${remind} до окончания пришлём напоминание.`;
});

const expDateText = computed<string>(() => {
  if (expKey.value === 'never') return 'Без срока';
  const days = expiresInDays();
  if (days === null) return 'Без срока';
  const target = new Date(Date.now() + days * 86400000);
  return target.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
});

const customMin = computed(() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
});
const customMax = computed(() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 2);
  return d.toISOString().slice(0, 10);
});

function applyPreset(p: { label: string; codes: string[] | 'all-but-admin' }) {
  const codes = p.codes;
  const next: Record<string, boolean> = {};
  for (const s of SCOPES) {
    if (codes === 'all-but-admin') {
      next[s.code] = s.code !== 'admin';
    } else {
      next[s.code] = codes.includes(s.code);
    }
  }
  scopesChecked.value = next;
}

async function submit(): Promise<void> {
  const trimmed = name.value.trim();
  if (!trimmed) {
    error.value = 'Введите имя ключа.';
    return;
  }
  if (selectedScopes.value.length === 0) {
    error.value = 'Выберите хотя бы один scope.';
    return;
  }
  error.value = null;
  submitting.value = true;
  modals.setPending(true);
  try {
    const out = await ApiKeysApi.create({
      name: trimmed,
      scopes: selectedScopes.value,
      expires_in_days: expiresInDays(),
    });
    created.value = out;
    modals.emit('add-api-key', out as ApiKey);
    step.value = 'reveal';
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 401) error.value = 'Сессия истекла. Перезайдите.';
      else if (e.status === 403)
        error.value = 'Недостаточно прав — для создания ключа нужна роль admin.';
      else if (e.status === 404)
        error.value = 'Endpoint /api/v1/api-keys ещё не задеплоен. Подождите релиз.';
      else if (e.status === 409) error.value = 'Ключ с таким именем уже существует.';
      else if (e.status === 422) error.value = e.message || 'Невалидные данные.';
      else if (e.status === 429) error.value = 'Слишком много запросов. Подождите минуту.';
      else error.value = e.message || `Ошибка сервера (${e.status})`;
    } else {
      error.value = (e as Error).message || 'Сеть недоступна.';
    }
  } finally {
    submitting.value = false;
    modals.setPending(false);
  }
}

async function copySecret() {
  if (!created.value) return;
  try {
    await navigator.clipboard.writeText(created.value.secret_plain);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1600);
  } catch {
    // fail-silent — браузер без clipboard API
  }
}

function close() {
  if (submitting.value) return;
  modals.close();
}

const snippet = computed<string>(() => {
  const key = created.value?.secret_plain || 'sk_live_…';
  const masked =
    key.length > 18 ? `${key.slice(0, 12)}…` : key;
  if (tab.value === 'python') {
    return [
      'import requests',
      '',
      'r = requests.get(',
      '    "https://link.helmix.app/api/v1/links",',
      `    headers={"Authorization": "Bearer ${masked}"},`,
      ')',
      'print(r.json())',
    ].join('\n');
  }
  if (tab.value === 'node') {
    return [
      'const res = await fetch(',
      '  "https://link.helmix.app/api/v1/links",',
      '  {',
      `    headers: { Authorization: "Bearer ${masked}" },`,
      '  },',
      ');',
      'console.log(await res.json());',
    ].join('\n');
  }
  return [
    'curl -X GET https://link.helmix.app/api/v1/links \\',
    `  -H "Authorization: Bearer ${masked}"`,
  ].join('\n');
});
</script>

<template>
  <UiModal
    :title="step === 'form' ? 'Создать ключ API' : 'Ключ создан'"
    :subtitle="
      step === 'form'
        ? 'Используется для интеграций — заголовок Authorization: Bearer sk_…'
        : 'Скопируйте сейчас — закроем модалку и больше не покажем.'
    "
    size="md"
    :disable-close="submitting"
    @close="close"
  >
    <!-- ─── Step 1: form ─── -->
    <section v-if="step === 'form'" class="step-form">
      <!-- Имя ключа -->
      <div class="field">
        <label class="field-label" for="ak-name">Имя ключа</label>
        <input
          id="ak-name"
          v-model="name"
          class="input"
          type="text"
          maxlength="255"
          placeholder="например, Zapier integration"
          :disabled="submitting"
          autocomplete="off"
        >
        <small class="field-hint">Видно только вам — помогает понять, где используется ключ.</small>
      </div>

      <!-- Окружение (display-only) -->
      <div class="field">
        <label class="field-label">Тип окружения</label>
        <div class="seg-row">
          <label class="seg-card" :class="{ active: env === 'live' }">
            <input v-model="env" type="radio" name="env" value="live" :disabled="submitting">
            <span class="seg-name">Production</span>
            <span class="seg-prefix">sk_live_…</span>
            <span class="seg-desc">Реальные данные, биллинг.</span>
          </label>
          <label class="seg-card" :class="{ active: env === 'test' }">
            <input v-model="env" type="radio" name="env" value="test" :disabled="submitting">
            <span class="seg-name">Sandbox</span>
            <span class="seg-prefix">sk_test_…</span>
            <span class="seg-desc">Тестовая среда, без оплат.</span>
          </label>
        </div>
      </div>

      <!-- Scopes -->
      <div class="field">
        <label class="field-label">Права доступа (scopes)</label>
        <div class="scope-list">
          <label
            v-for="s in SCOPES"
            :key="s.code"
            class="scope-row"
            :class="{ danger: s.danger }"
          >
            <input
              v-model="scopesChecked[s.code]"
              type="checkbox"
              :disabled="submitting"
            >
            <div class="scope-text">
              <span class="scope-code">{{ s.code }}</span>
              <span class="scope-desc">{{ s.desc }}</span>
            </div>
            <span class="scope-tier" :class="s.tier">{{ s.tier.toUpperCase() }}</span>
          </label>
        </div>
        <div class="scope-actions">
          <button
            v-for="p in PRESETS"
            :key="p.label"
            type="button"
            class="link-btn"
            :disabled="submitting"
            @click="applyPreset(p)"
          >
            {{ p.label }}
          </button>
        </div>
      </div>

      <!-- Expiry -->
      <div class="field">
        <label class="field-label">Срок действия</label>
        <div class="exp-row">
          <label
            v-for="opt in (['30','90','365','custom','never'] as ExpKey[])"
            :key="opt"
            class="exp-pill"
            :class="{ active: expKey === opt, warn: opt === 'never' }"
          >
            <input
              v-model="expKey"
              type="radio"
              name="exp"
              :value="opt"
              :disabled="submitting"
            >
            {{ opt === '30' ? '30 дней' : opt === '90' ? '90 дней' : opt === '365' ? '1 год' : opt === 'custom' ? 'Произвольно…' : 'Без срока' }}
          </label>
        </div>
        <input
          v-if="expKey === 'custom'"
          v-model="customDate"
          class="input date-input"
          type="date"
          :min="customMin"
          :max="customMax"
          :disabled="submitting"
        >
        <small class="field-hint" v-html="expHint" />
      </div>

      <!-- Auto-rotation (info-only) -->
      <div class="field toggle-field">
        <label class="toggle-row">
          <span class="toggle">
            <input v-model="autoRotate" type="checkbox" :disabled="submitting">
            <span class="toggle-slider" />
          </span>
          <div class="t-text">
            <span class="t-title">Авто-ротация (скоро)</span>
            <span class="t-desc">
              Раз в 90 дней выпустим новый ключ и пришлём на email — старый
              продолжит работать 7 дней для миграции. Эта опция пока сохраняется
              локально, серверная rotation — в следующем sprint'е.
            </span>
          </div>
        </label>
      </div>

      <!-- Warning -->
      <div class="warning-row">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>Ключ покажем <strong>один раз</strong>. Сохраните его сразу — после закрытия модалки будет недоступен.</span>
      </div>

      <p v-if="error" class="form-msg err" role="alert">{{ error }}</p>
    </section>

    <!-- ─── Step 2: reveal ─── -->
    <section v-else class="step-reveal">
      <div class="success-icon">
        <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>
      <div class="success-head">
        <h3>Ключ создан</h3>
        <p>Скопируйте и сохраните — больше мы его не покажем.</p>
      </div>

      <div class="key-box">
        <code>{{ created?.secret_plain || '' }}</code>
        <UiButton variant="outline" size="sm" @click="copySecret">
          {{ copied ? 'Скопировано ✓' : 'Скопировать' }}
        </UiButton>
      </div>

      <div class="key-summary">
        <div class="ks-row">
          <span class="ks-label">Имя:</span>
          <span class="ks-val">{{ created?.name || name }}</span>
        </div>
        <div class="ks-row">
          <span class="ks-label">Scopes:</span>
          <span class="ks-val">{{ (created?.scopes || selectedScopes).join(', ') || '—' }}</span>
        </div>
        <div class="ks-row">
          <span class="ks-label">Действует до:</span>
          <span class="ks-val">{{ expDateText }}</span>
        </div>
        <div class="ks-row">
          <span class="ks-label">Окружение:</span>
          <span class="ks-val">{{ env === 'live' ? 'Production · биллинговые операции включены' : 'Sandbox · без биллинга' }}</span>
        </div>
      </div>

      <div class="snippet">
        <div class="snippet-head">
          <span>Пример использования</span>
          <button
            v-for="t in (['curl','python','node'] as const)"
            :key="t"
            type="button"
            class="snippet-tab"
            :class="{ active: tab === t }"
            @click="tab = t"
          >
            {{ t === 'curl' ? 'curl' : t === 'python' ? 'Python' : 'Node.js' }}
          </button>
        </div>
        <pre><code>{{ snippet }}</code></pre>
      </div>
    </section>

    <template #footer>
      <template v-if="step === 'form'">
        <UiButton variant="ghost" :disabled="submitting" @click="close">Отмена</UiButton>
        <UiButton
          variant="primary"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        >
          Создать ключ
        </UiButton>
      </template>
      <UiButton v-else variant="primary" @click="close">
        Готово — я сохранил ключ
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.step-form,
.step-reveal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fg-muted);
}
.field-hint {
  font-size: 11.5px;
  color: var(--fg-muted);
  line-height: 1.45;
}
.field-hint :deep(strong) {
  color: var(--fg);
  font-weight: 600;
}

.input {
  width: 100%;
  padding: 9px 12px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: 7px;
  color: var(--fg);
  font-size: 13.5px;
  font-family: inherit;
  line-height: 1.5;
}
.input:focus {
  outline: none;
  border-color: var(--accent);
}
.input:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.date-input {
  width: auto;
  max-width: 220px;
}

/* Env segments */
.seg-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.seg-card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  transition: 0.1s;
  position: relative;
}
.seg-card input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.seg-card:hover {
  border-color: var(--accent);
}
.seg-card.active {
  border-color: var(--accent);
  background: var(--accent-soft);
}
.seg-name {
  font-size: 13.5px;
  font-weight: 600;
}
.seg-prefix {
  font-family: var(--font-mono, "SF Mono", Menlo, monospace);
  font-size: 11.5px;
  color: var(--accent-hi);
}
.seg-desc {
  font-size: 11.5px;
  color: var(--fg-muted);
  line-height: 1.4;
}

/* Scope list */
.scope-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
}
.scope-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.1s;
}
.scope-row:hover {
  background: var(--bg);
}
.scope-row input {
  margin-top: 3px;
  accent-color: var(--accent);
  flex-shrink: 0;
}
.scope-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.scope-code {
  font-family: var(--font-mono, "SF Mono", Menlo, monospace);
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fg);
}
.scope-desc {
  font-size: 11.5px;
  color: var(--fg-muted);
  line-height: 1.4;
}
.scope-tier {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 10px;
  font-weight: 800;
  font-family: var(--font-mono, "SF Mono", Menlo, monospace);
}
.scope-tier.r {
  background: rgba(77, 214, 147, 0.15);
  color: var(--success);
}
.scope-tier.w {
  background: rgba(255, 139, 77, 0.15);
  color: #ff8b4d;
}
.scope-tier.a {
  background: var(--danger-soft);
  color: var(--danger);
}
.scope-row.danger {
  background: var(--danger-soft);
}
.scope-row.danger:hover {
  background: var(--danger-soft);
}
.scope-row.danger input {
  accent-color: var(--danger);
}

.scope-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.link-btn {
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--accent-hi);
  font-size: 12px;
  padding: 4px 10px;
  font-family: inherit;
  border-radius: 4px;
}
.link-btn:hover:not(:disabled) {
  background: var(--bg-card);
}
.link-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Exp pills */
.exp-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.exp-pill {
  padding: 6px 12px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 100px;
  font-size: 12.5px;
  cursor: pointer;
  color: var(--fg-muted);
  transition: 0.1s;
  position: relative;
}
.exp-pill input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.exp-pill:hover {
  color: var(--fg);
}
.exp-pill.active {
  background: var(--accent-soft);
  color: var(--accent-hi);
  border-color: transparent;
  font-weight: 600;
}
.exp-pill.warn.active {
  background: var(--warning-soft);
  color: var(--warning);
}

/* Toggle */
.toggle-field {
  padding-top: 2px;
}
.toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.toggle {
  position: relative;
  width: 32px;
  height: 18px;
  flex-shrink: 0;
  margin-top: 2px;
}
.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}
.toggle-slider {
  position: absolute;
  inset: 0;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 100px;
  transition: 0.15s;
}
.toggle-slider::before {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  left: 2px;
  top: 2px;
  background: var(--fg);
  border-radius: 50%;
  transition: 0.15s;
}
.toggle input:checked + .toggle-slider {
  background: var(--accent);
  border-color: var(--accent);
}
.toggle input:checked + .toggle-slider::before {
  transform: translateX(14px);
  background: #fff;
}
.t-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.t-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--fg);
}
.t-desc {
  font-size: 11.5px;
  color: var(--fg-muted);
  line-height: 1.45;
}

/* Warning */
.warning-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  background: var(--warning-soft);
  border: 1px solid color-mix(in srgb, var(--warning) 30%, transparent);
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--fg);
}
.warning-row svg {
  color: var(--warning);
  flex-shrink: 0;
  margin-top: 1px;
}
.warning-row strong {
  color: var(--fg);
  font-weight: 700;
}

.form-msg {
  font-size: 13px;
  margin: 0;
}
.form-msg.err {
  color: var(--danger);
}

/* Reveal step */
.success-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(77, 214, 147, 0.15);
  color: var(--success);
  display: grid;
  place-items: center;
}
.success-head {
  text-align: center;
  margin-bottom: 4px;
}
.success-head h3 {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 4px;
  color: var(--fg);
}
.success-head p {
  font-size: 13px;
  color: var(--fg-muted);
  margin: 0;
}

.key-box {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 14px;
  background: var(--bg-card);
  border: 1px dashed var(--accent);
  border-radius: 8px;
}
.key-box code {
  flex: 1;
  min-width: 0;
  font-family: var(--font-mono, "SF Mono", Menlo, monospace);
  font-size: 12.5px;
  color: var(--fg);
  word-break: break-all;
}

.key-summary {
  display: flex;
  flex-direction: column;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.ks-row {
  display: grid;
  grid-template-columns: 130px 1fr;
  padding: 8px 14px;
  font-size: 12.5px;
  border-bottom: 1px solid var(--border);
}
.ks-row:last-child {
  border-bottom: 0;
}
.ks-label {
  color: var(--fg-muted);
}
.ks-val {
  color: var(--fg);
  word-break: break-word;
}

.snippet {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}
.snippet-head {
  display: flex;
  gap: 4px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  align-items: center;
  background: var(--bg-elev);
  font-size: 11.5px;
  color: var(--fg-muted);
}
.snippet-head span:first-child {
  margin-right: auto;
  font-weight: 600;
}
.snippet-tab {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 4px 10px;
  font-size: 11.5px;
  color: var(--fg-dim);
  border-radius: 5px;
  font-family: inherit;
}
.snippet-tab.active {
  background: var(--bg-card);
  color: var(--fg);
}
.snippet pre {
  margin: 0;
  padding: 12px 14px;
  overflow-x: auto;
}
.snippet code {
  font-family: var(--font-mono, "SF Mono", Menlo, monospace);
  font-size: 11.5px;
  color: var(--fg);
  white-space: pre-wrap;
  word-break: break-all;
}

@media (max-width: 640px) {
  .seg-row {
    grid-template-columns: 1fr;
  }
  .ks-row {
    grid-template-columns: 1fr;
    gap: 2px;
    padding: 8px 12px;
  }
  .ks-label {
    font-size: 11px;
  }
}
</style>
